import type { Database } from 'sql.js';
import { newPrefixedPublicId } from '@core/domain/secureRandom.ts';
import { assertPuntoEntregaParticipanteCatalogUniqueness } from '@core/infrastructure/driven/sqljs/sqliteEntregaClaimConstraints.ts';
import {
  insertRowId,
  persistBrowserDataUrl,
  queryAll,
  queryOne,
  type SqlJsBind,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type {
  PuntoEntregaParticipanteFormPayload,
  SavePuntoEntregaPayload,
} from '../../../domain/puntoEntregaModel.ts';
import { isBonoAlreadyRedimido } from '@core/infrastructure/driven/sqljs/sqlJsBonoFkFromCaption.ts';

type SqlBind = SqlJsBind;

function getParticipanteRow(db: Database, id: number): Record<string, SqlBind> | null {
  return queryOne(db, 'SELECT * FROM participantes WHERE id = ?', [id]);
}

function updatePuntoHeaderOnly(
  db: Database,
  puntoId: number,
  payload: SavePuntoEntregaPayload,
  esRem: number,
): void {
  db.run(
    `UPDATE punto_entrega SET lugar_id = ?, lugar_origen = ?, entrega_id = ?, es_remanente = ?,
       informe_pdf = ?, fecha_hora = ?, latitud = ?, longitud = ?, direccion = ?, responsable_id = ? WHERE id = ?`,
    [
      payload.lugarId,
      payload.lugarOrigen,
      payload.entregaId,
      esRem,
      persistBrowserDataUrl(payload.informePdf),
      payload.fechaHora,
      payload.latitud ?? '',
      payload.longitud ?? '',
      payload.direccion ?? '',
      payload.responsableId,
      puntoId,
    ],
  );
}

export function purgeParticipantesYAcudientes(db: Database, puntoId: number): void {
  const acudRows = queryAll(
    db,
    `SELECT DISTINCT acudiente_id AS aid FROM punto_entrega_participante
     WHERE punto_entrega_id = ? AND acudiente_id IS NOT NULL`,
    [puntoId],
  );
  const acudienteIds = [
    ...new Set(
      acudRows
        .map((r) => Number(r.aid))
        .filter((idV) => Number.isFinite(idV) && idV > 0),
    ),
  ];
  db.run('DELETE FROM punto_entrega_participante WHERE punto_entrega_id = ?', [puntoId]);
  for (const aid of acudienteIds) {
    db.run('DELETE FROM acudientes WHERE id = ?', [aid]);
  }
}

/** Inserts a delivery point with `pe_<uuid>` `public_id` (offline sync ingest examples). */
function insertNewPuntoRow(db: Database, payload: SavePuntoEntregaPayload, esRem: number): number {
  return insertRowId(
    db,
    `INSERT INTO punto_entrega (public_id, lugar_id, lugar_origen, entrega_id, es_remanente, informe_pdf, fecha_hora, latitud, longitud, direccion, responsable_id)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      newPrefixedPublicId('pe'),
      payload.lugarId,
      payload.lugarOrigen,
      payload.entregaId,
      esRem,
      persistBrowserDataUrl(payload.informePdf),
      payload.fechaHora,
      payload.latitud ?? '',
      payload.longitud ?? '',
      payload.direccion ?? '',
      payload.responsableId,
    ],
  );
}

function insertAcudienteFromPepLine(
  db: Database,
  line: PuntoEntregaParticipanteFormPayload,
  participante: Record<string, SqlBind>,
  insAcudSql: string,
): {
  acudienteId: number | null;
  esAcud: number;
} {
  let acudienteId: number | null = null;
  let esAcud = 0;

  if (line.presentaAcudienteAutorizado && line.acudiente) {
    esAcud = 1;
    const a = line.acudiente;
    acudienteId = insertRowId(db, insAcudSql, [
      newPrefixedPublicId('acu'),
      a.nombre,
      Number(participante.id),
      a.parentescoId,
      persistBrowserDataUrl(a.documentoFrontal),
      persistBrowserDataUrl(a.documentoReverso),
      persistBrowserDataUrl(a.documentoAutorizadoFrontal),
      persistBrowserDataUrl(a.documentoAutorizadoReverso),
      persistBrowserDataUrl(a.autorizado),
    ]);
  }

  return { acudienteId, esAcud };
}

function assertParticipantesClaimAllowedForPunto(
  db: Database,
  puntoId: number,
  lines: PuntoEntregaParticipanteFormPayload[],
): void {
  for (const line of lines) {
    if (!getParticipanteRow(db, line.participanteId)) continue;

    assertPuntoEntregaParticipanteCatalogUniqueness(db, puntoId, line.participanteId);
  }
}

function persistOneParticipanteLine(
  db: Database,
  puntoId: number,
  line: PuntoEntregaParticipanteFormPayload,
  insPepSql: string,
  insAcudSql: string,
): void {
  const participante = getParticipanteRow(db, line.participanteId);
  if (!participante) return;

  const { acudienteId, esAcud } = insertAcudienteFromPepLine(db, line, participante, insAcudSql);

  const actaPath = persistBrowserDataUrl(line.acta);
  const firmaPath = persistBrowserDataUrl(line.firmaBase64);
  const fotoPath = persistBrowserDataUrl(line.registroFotografico);

  const bonoId = line.bonoId != null && line.bonoId > 0 ? line.bonoId : null;
  if (bonoId == null || bonoId <= 0) {
    throw new Error(
      `Participante ${line.participanteId}: indique el bono a registrar en el punto de entrega.`,
    );
  }
  if (isBonoAlreadyRedimido(db, bonoId)) {
    throw new Error(
      `El bono (id ${bonoId}) ya fue redimido; no puede asignarse en el punto de entrega.`,
    );
  }

  const tipoBono = String(line.tipoBono ?? '').trim();

  const pepPub = newPrefixedPublicId('pep');
  let pepId = insertRowId(db, insPepSql, [
    pepPub,
    puntoId,
    line.participanteId,
    acudienteId,
    esAcud,
    line.asistencia ? 1 : 0,
    line.capacitado ? 1 : 0,
    actaPath,
    line.politicaTratamientos ? 1 : 0,
    firmaPath,
    fotoPath,
    line.observaciones ?? '',
    line.entregaBolsa ? 1 : 0,
    bonoId,
    tipoBono,
  ]);
  if (!pepId) {
    const row = queryOne(db, 'SELECT last_insert_rowid() AS k', []);
    pepId = Number(row?.k ?? 0);
  }
}

function persistParticipantesAfterPuntoHeader(db: Database, puntoId: number, payload: SavePuntoEntregaPayload): void {
  const insPepSql = `INSERT INTO punto_entrega_participante (
      public_id, punto_entrega_id, participante_id,
      acudiente_id, es_acudiente,
      asistencia, capacitado, acta, politica_tratamientos, firma_base64, registro_fotografico,
      observaciones, entrega_bolsa, bono_id, tipo_bono
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;

  const insAcudSql = `INSERT INTO acudientes (
      public_id, nombre, participante_id, parentesco_id,
      documento_beneficiario_frontal_path, documento_beneficiario_reverso_path,
      documento_autorizado_frontal_path, documento_autorizado_reverso_path,
      autorizado_path, estado
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`;

  for (const line of payload.participantes ?? []) {
    persistOneParticipanteLine(db, puntoId, line, insPepSql, insAcudSql);
  }
}

export function savePuntoEntregaTransaction(
  db: Database,
  schedulePersist: () => void,
  payload: SavePuntoEntregaPayload,
): number {
  const esRem = payload.esRemanente ? 1 : 0;
  db.run('BEGIN IMMEDIATE');
  try {
    let puntoId = payload.id;
    if (puntoId) {
      const exists = queryOne(db, 'SELECT id FROM punto_entrega WHERE id = ?', [puntoId]);
      if (!exists) {
        db.run('ROLLBACK');
        throw new Error(`punto_entrega ${puntoId} no existe`);
      }
      updatePuntoHeaderOnly(db, puntoId, payload, esRem);
    } else {
      puntoId = insertNewPuntoRow(db, payload, esRem);
    }
    assertParticipantesClaimAllowedForPunto(db, puntoId, payload.participantes ?? []);
    if (payload.id) {
      purgeParticipantesYAcudientes(db, puntoId);
    }
    persistParticipantesAfterPuntoHeader(db, puntoId, payload);
    db.run('COMMIT');
    schedulePersist();
    return puntoId;
  } catch (e) {
    try {
      db.run('ROLLBACK');
    } catch {
      /* ignore */
    }
    throw e;
  }
}
